import { useParams } from 'react-router-dom';
import { useSplitViewContext } from '@/components/layout/SplitViewContent';

/**
 * A hook that returns route params, supporting both normal routing and split view mode.
 * 
 * In normal routing mode, it uses React Router's useParams().
 * In split view mode, it uses params from the SplitViewContext.
 * 
 * This allows page components to work seamlessly in both contexts.
 * 
 * @example
 * // In a page component:
 * const { itemId } = useSplitViewAwareParams<{ itemId: string }>();
 */
export function useSplitViewAwareParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const routerParams = useParams<T>();
  const splitViewContext = useSplitViewContext();

  // If we're in a split view and the router params are empty, use split view params
  if (splitViewContext?.isSplitView) {
    // Merge: split view params take precedence for any key that's defined there
    return {
      ...routerParams,
      ...splitViewContext.params,
    } as T;
  }

  return routerParams as T;
}

/**
 * Hook to check if the current component is being rendered in a split view.
 */
export function useIsSplitView(): boolean {
  const splitViewContext = useSplitViewContext();
  return splitViewContext?.isSplitView ?? false;
}

